import { BigNumber } from '@ethersproject/bignumber';

/**
 * Calculates the spread percentage between ask and bid prices.
 *
 * The spread represents the difference between the sell (ask) price and buy (bid) price,
 * expressed as a percentage of the bid price. This is commonly used to measure market liquidity.
 *
 * Formula: |(ask - bid) / bid| * 100
 *
 * Uses @ethersproject/bignumber for precise calculations with 18 decimal places precision.
 * Returns the result with MAXIMUM precision (no rounding, no truncation) - critical for trading accuracy.
 *
 * @param ask - The ask (sell) price as a string or number
 * @param bid - The bid (buy) price as a string or number
 * @returns The spread percentage with maximum precision, or null if calculation fails
 *
 * @example
 * ```typescript
 * calculateSpreadPercentage('100.50', '100.00') // Returns '0.5'
 * calculateSpreadPercentage('0.00001234', '0.00001230') // Returns full precision
 * calculateSpreadPercentage('100', '0') // Returns null (division by zero)
 * ```
 */
export function calculateSpreadPercentage(
  ask: string | number,
  bid: string | number,
): string | null {
  try {
    // Convert to strings and remove any non-numeric characters except decimal point
    const askStr = String(ask).trim();
    const bidStr = String(bid).trim();

    // Validate inputs
    if (!askStr || !bidStr || askStr === 'NaN' || bidStr === 'NaN') {
      return null;
    }

    // For BigNumber we need to work with integers, so we'll scale up by a large factor
    // Using 18 decimals (1e18) like ethers does for wei
    const PRECISION = BigNumber.from(10).pow(18);
    const HUNDRED = BigNumber.from(100);

    // Parse the decimal strings by splitting on decimal point
    const parseDecimal = (value: string): BigNumber => {
      const [integer, decimal = '0'] = value.split('.');
      // Pad or truncate decimal to 18 places
      const decimalPadded = decimal.padEnd(18, '0').slice(0, 18);
      const fullValue = integer + decimalPadded;
      return BigNumber.from(fullValue);
    };

    const askBN = parseDecimal(askStr);
    const bidBN = parseDecimal(bidStr);
    const bidAbs = bidBN.lt(0) ? bidBN.mul(-1) : bidBN;

    // Check for zero bid (division by zero)
    if (bidAbs.isZero()) {
      return null;
    }

    // Calculate: |(ask - bid) / bid| * 100
    // difference = ask - bid
    let difference = askBN.sub(bidBN);

    // Take absolute value
    if (difference.lt(0)) {
      difference = difference.mul(-1);
    }

    // Multiply by 100 * PRECISION to maintain precision during division
    const numerator = difference.mul(HUNDRED).mul(PRECISION);

    // Divide by absolute bid to keep result positive for negative prices
    const resultBN = numerator.div(bidAbs);

    // Convert back to decimal string with MAXIMUM precision (no artificial rounding)
    // resultBN is scaled by PRECISION (18 decimals)
    const resultStr = resultBN.toString().padStart(19, '0');
    const integerPart = resultStr.slice(0, -18) || '0';
    const decimalPart = resultStr.slice(-18);

    // Remove trailing zeros for clean output but preserve all significant digits
    const cleanDecimal = decimalPart.replace(/0+$/, '');

    // Return with maximum precision
    if (cleanDecimal === '') {
      return integerPart;
    }

    return `${integerPart}.${cleanDecimal}`;
  } catch (error) {
    // Return null if input is invalid or calculation fails
    console.error('Failed to calculate spread percentage:', error);
    return null;
  }
}
